import {
  map,
  get,
  times,
  isEmpty,
  compact,
  filter,
  remove,
  includes,
  sortBy,
  findIndex,
  find,
  chunk,
} from 'lodash';
import { connect } from 'react-redux';

import CollectionParent from 'components/CollectionParent';
import { BREADCRUMB_NAVIGATION_LINK_TYPES } from 'constants/breadcrumbNavigation';
import {
  COLLECTION_ITEM_TYPES,
  COLLECTION_N_COLUMNS,
  COLLECTION_STORY_CARDS_TARGETING,
  COLLECTION_STORY_CARDS_VISIBILITY,
} from 'constants/collection';
import { CAMPAIGN_NAMES, PERSONALIZATION_STATUS } from 'constants/personalizations';
import { SHOP_IDS } from 'constants/shops';
import BooleanExperimentCampaign from 'models/BooleanExperimentCampaign';
import DYRecommendationsModel from 'models/DYRecommendations';
import Catalog from 'state/selectors/catalog';
import CollectionPage from 'state/selectors/collection-page';
import PersonalizationsSelector from 'state/selectors/personalizations';
import BreadcrumbsUtil from 'utils/Breadcrumbs';
import Filters from 'utils/Filters';
import { insertStoryCards, removeDuplicateAndOutOfBoundsPlacements } from 'utils/ParentCollection';
import Settings from 'utils/Settings';

const N_PLACEHOLDER_ITEMS = 7;

const mapStateToProps = (state, { handle, features }) => {
  const parentCollection = Catalog.getParentCollection(state, handle);
  const isParentCollectionLoading = Catalog.isParentCollectionLoading(state, handle);

  if (!parentCollection && !isParentCollectionLoading) {
    throw new Error(`Could not find parent collection with handle "${handle}"`);
  }

  const availableColorways = isParentCollectionLoading
    ? []
    : Catalog.getAvailableParentCollectionColorways(state, handle);
  const parentCollectionCacheKey = Catalog.getParentCollectionCacheKey(state, handle);
  const cacheKey = CollectionPage.getCollectionFilterCacheKey(state, parentCollectionCacheKey);

  const filteredParentCollectionColorways = Filters.executeFilters({
    items: availableColorways,
    filterItems: CollectionPage.getFilteringEngineFilterItems(state),
    cacheKey,
  });
  const isParentCollectionEmpty = isEmpty(filteredParentCollectionColorways);

  const hasAppliedFilterOption = CollectionPage.hasAppliedFilterOption(state);
  const isFiltering = hasAppliedFilterOption;

  const { secondaryNavigationOptions } = BreadcrumbsUtil.getSecondaryNavigationOptions({
    type: BREADCRUMB_NAVIGATION_LINK_TYPES.COLLECTION,
    handle: get(parentCollection, 'handle'),
  });

  const hasSecondaryNavigationOptions = !isEmpty(secondaryNavigationOptions);
  const targetedPageSectionsCampaignData =
    PersonalizationsSelector.getTargetedPageSectionCampaignData(
      state,
      CAMPAIGN_NAMES.MULTI_TOUCH_CAMPAIGN,
      parentCollection.experimentPageSections
    );

  // Personalized Colorway Card Experiment
  const colorwayExperimentVariation = PersonalizationsSelector.getCampaignVariationData(
    state,
    CAMPAIGN_NAMES.COLLECTION_COLORWAY_CARDS
  );
  const isColorwayExperimentLoading = PersonalizationsSelector.getIsExperimentLoading(
    state,
    CAMPAIGN_NAMES.COLLECTION_COLORWAY_CARDS
  );
  const showColorwayCards =
    (!isColorwayExperimentLoading &&
      get(colorwayExperimentVariation, ['payload', 'data', 'enabled'], false) === 'true') ||
    Settings.shopID === SHOP_IDS.UK;
  const isLoading =
    isParentCollectionLoading ||
    isColorwayExperimentLoading ||
    targetedPageSectionsCampaignData?.isLoading;

  let collectionItems;
  let targetedTopBanner;

  if (isLoading) {
    collectionItems = times(N_PLACEHOLDER_ITEMS, () => ({
      type: COLLECTION_ITEM_TYPES.CHILD_COLLECTION_PLACEHOLDER,
    }));
  } else {
    const parentCollectionItems =
      parentCollection.parentCollectionItems ?? parentCollection.masterProducts;
    collectionItems = map(parentCollectionItems, item => {
      const { type, id, data } = item;
      if (type === COLLECTION_ITEM_TYPES.STORY_CARD) {
        return { type: COLLECTION_ITEM_TYPES.STORY_CARD, id, data };
      }

      const masterProductHandle = id ?? item;
      const availableMasterProductColorways = Catalog.getAvailableMasterProductColorways(
        state,
        masterProductHandle
      );
      const masterProductCacheKey = Catalog.getMasterProductCacheKey(state, masterProductHandle);
      const masterProductFilterCacheKey = CollectionPage.getCollectionFilterCacheKey(
        state,
        masterProductCacheKey
      );

      const filteredMasterProductColorways = Filters.executeFilters({
        items: availableMasterProductColorways,
        filterItems: CollectionPage.getFilteringEngineFilterItems(state),
        cacheKey: masterProductFilterCacheKey,
      });

      const isMasterProductEmpty = isEmpty(filteredMasterProductColorways);

      if (isMasterProductEmpty) {
        return null;
      }

      if (type === COLLECTION_ITEM_TYPES.COLORWAY) {
        const selectedColorway = find(filteredMasterProductColorways, { handle: item.colorway });

        if (!showColorwayCards || !selectedColorway) {
          return null;
        }

        return {
          type: COLLECTION_ITEM_TYPES.CHILD_COLLECTION,
          id: masterProductHandle,
          initialColorwayHandle: item.colorway,
          productType: type,
        };
      }

      return { type: COLLECTION_ITEM_TYPES.CHILD_COLLECTION, id: masterProductHandle };
    });
  }

  // Remove any empty collections from the list of items
  collectionItems = compact(collectionItems);

  const filterIdentifiers = Filters.determineRelevantCollectionPageFilterIdentifiers({
    items: availableColorways,
    cacheKey: parentCollectionCacheKey,
  });

  const showFilterToggle = !isEmpty(filterIdentifiers);

  const topBanner = get(parentCollection, ['topBanner', 'fields']);
  const secondRowBanner = get(parentCollection, ['secondRowBanner', 'fields']);
  const bottomBanner = get(parentCollection, ['bottomBanner', 'fields']);
  const faq = {
    title: get(parentCollection, ['faqHeading']),
    items: get(parentCollection, ['faqItems']),
  };

  const rightPillToggle = get(parentCollection, ['rightPillToggle', 'fields']);
  const leftPillToggle = get(parentCollection, ['leftPillToggle', 'fields']);
  const showPillToggle = !!leftPillToggle && !!rightPillToggle;
  const descriptionModule = get(parentCollection, ['descriptionModule']);

  const storyCardPlacementsData = get(parentCollection, ['storyCardPlacements'], []);
  let storyCardPlacements = [];

  const alwaysRenderedStoryCardPlacements = filter(
    storyCardPlacementsData,
    ({ targeting }) => targeting === COLLECTION_STORY_CARDS_TARGETING.ALWAYS
  );
  storyCardPlacements.push(...alwaysRenderedStoryCardPlacements);

  if (targetedPageSectionsCampaignData?.activePageSections?.length > 0) {
    // Story Cards Targeting
    const targetedStoryCardPlacements = filter(
      targetedPageSectionsCampaignData?.activePageSections,
      ({ _contentType }) => _contentType === 'storyCardPlacement'
    );

    storyCardPlacements.push(...targetedStoryCardPlacements);

    // Top Banner Targeting
    targetedTopBanner = targetedPageSectionsCampaignData?.activePageSections?.find(
      ({ _contentType }) => _contentType === 'collectionsBanner'
    );
  }

  if (isFiltering) {
    // If not in a variation or filtering, remove all Story Cards
    remove(
      collectionItems,
      item =>
        item.type === COLLECTION_ITEM_TYPES.STORY_CARD ||
        item.type === COLLECTION_ITEM_TYPES.STORY_CARD_V2
    );
  }

  let mobileRows = chunk(collectionItems, COLLECTION_N_COLUMNS.TWO);
  let desktopRows = chunk(collectionItems, COLLECTION_N_COLUMNS.THREE);

  if (!isFiltering && !isLoading) {
    const visibleMobileStoryCardPlacements = storyCardPlacements.filter(({ localizedVisibility }) =>
      includes(localizedVisibility, COLLECTION_STORY_CARDS_VISIBILITY.MOBILE)
    );
    const visibleDesktopStoryCardPlacements = storyCardPlacements.filter(
      ({ localizedVisibility }) =>
        includes(localizedVisibility, COLLECTION_STORY_CARDS_VISIBILITY.DESKTOP)
    );

    const uniqueMobileStoryCardPlacements = removeDuplicateAndOutOfBoundsPlacements(
      visibleMobileStoryCardPlacements,
      mobileRows.length
    );
    const uniqueDesktopStoryCardPlacements = removeDuplicateAndOutOfBoundsPlacements(
      visibleDesktopStoryCardPlacements,
      desktopRows.length
    );

    mobileRows = insertStoryCards(
      mobileRows,
      uniqueMobileStoryCardPlacements,
      COLLECTION_N_COLUMNS.TWO
    );
    desktopRows = insertStoryCards(
      desktopRows,
      uniqueDesktopStoryCardPlacements,
      COLLECTION_N_COLUMNS.THREE
    );
  }

  // Personalized Merchandising Experiment
  const personalizedMerchandisingCampaignData = PersonalizationsSelector.getCampaignByName(
    state,
    CAMPAIGN_NAMES.COLLECTION_PERSONALIZED_MERCHANDISING
  );

  if (personalizedMerchandisingCampaignData?.status === PERSONALIZATION_STATUS.SUCCESS) {
    const personalizedMerchandisingCampaign = new DYRecommendationsModel(
      personalizedMerchandisingCampaignData
    );

    if (!isEmpty(personalizedMerchandisingCampaign.products)) {
      // Attempt to sort the Collection using our DY Recommendations
      const sortedCollectionItems = sortBy(collectionItems, item => {
        const index = findIndex(personalizedMerchandisingCampaign.products, [
          'productData.parent_handle',
          item.id,
        ]);
        return index === -1 ? Infinity : index;
      });

      // Add additional initial colorway ordering info
      collectionItems = map(sortedCollectionItems, item => {
        const matchingProduct = find(personalizedMerchandisingCampaign.products, [
          'productData.parent_handle',
          item.id,
        ]);
        const updatedItem = item;

        if (matchingProduct && !updatedItem.initialColorwayHandle) {
          updatedItem.initialColorwayHandle = get(matchingProduct, ['productData', 'handle']);
        }

        return updatedItem;
      });
    }
  }

  // Sticky Filter Experiment
  const stickyFilterCampaign = PersonalizationsSelector.getCampaignByName(
    state,
    CAMPAIGN_NAMES.COLLECTION_STICKY_SUBNAV
  );
  const {
    dynamicYieldIdentifiers: stickyFilterCampaignData,
    experimentEngagementIdentifiers: stickyFilterCampaignEngagementIdentifiers,
  } = new BooleanExperimentCampaign({
    campaign: stickyFilterCampaign,
  });

  return {
    bottomBanner,
    collectionItems,
    faq,
    features,
    hasSecondaryNavigationOptions,
    isFiltering,
    isEmpty: isParentCollectionEmpty,
    isLoading,
    secondRowBanner,
    showFilterToggle,
    leftPillToggle,
    rightPillToggle,
    showPillToggle,
    stickyFilterCampaignData,
    stickyFilterCampaignEngagementIdentifiers,
    storyCardPlacements,
    topBanner: targetedTopBanner ?? topBanner,
    mobileRows,
    desktopRows,
    descriptionModule,
  };
};

export default connect(mapStateToProps)(CollectionParent);
